import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { showToast } from "@/other/utils/utils";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import { extractAsyncTaskId } from "@/modern/components/async-tasks/utils";
import {
  useCoverageActivityQuery,
  useCoverageHubAssetsQuery,
  useCoverageHubDetailQuery,
  useCoverageHubsQuery,
  useCoverageHubWorkFilesQuery,
  useCoverageMutations,
  useCoverageSummaryQuery,
} from "@/react/features/coverage/api/coverage.api";
import type {
  CoverageFilter as ApiCoverageFilter,
  CoverageActivityRange,
  CoverageHubRow,
  CoverageWorkFile,
} from "@/react/features/coverage/api/types";
import type { BucketFill, CoverageAsset, ProblemHub } from "../types";
import { mapApiAsset } from "../types";

export type CoverageFilter = ApiCoverageFilter;

export interface KpiData {
  requiredFilled: number;
  requiredTotal: number;
  requiredPercent: number;
  outputReadyCount: number;
  sequenceReadyCount: number;
  blindSpotCount: number;
  hubsTotal: number;
}

export interface PromotedAssetSummary {
  id: number;
  caption: string;
  createdAt: Date;
  tagLabels: string[];
  problemHubLabels: string[];
}

export interface UserPromotionData {
  userId: number;
  name: string;
  profileImageURL?: string;
  promotions: number;
  promotedAssets: PromotedAssetSummary[];
}

export type PromotionDateRange = CoverageActivityRange;

function mapHubRowToProblemHub(row: CoverageHubRow): ProblemHub {
  return {
    id: String(row.id),
    name: row.label,
    description: row.description,
    buckets: [],
    sequenceStatus: row.sequence_status,
    filledRequired: row.filled_required,
    totalRequired: row.total_required,
    overallStatus: row.overall_status,
    progressPercent: row.progress_percent,
    temporalAssets: { before: [], during: [], after: [] },
  };
}

/**
 * Join hub-detail bucket/temporal id lists to the hub assets page.
 * Does not invent labels, counts, or statuses — those come from the detail API.
 */
function buildSelectedProblemHub(
  detail: NonNullable<ReturnType<typeof useCoverageHubDetailQuery>["data"]>,
  assets: CoverageAsset[]
): ProblemHub {
  const byId = new Map(assets.map((a) => [a.id, a]));
  const resolve = (ids: number[]) =>
    ids.map((id) => byId.get(id)).filter(Boolean) as CoverageAsset[];

  const buckets: BucketFill[] = (detail.buckets ?? []).map((b) => ({
    tagId: b.tag_id,
    label: b.label,
    assetCount: b.asset_count,
    status: b.status,
    assets: resolve(b.asset_ids ?? []),
  }));

  return {
    id: String(detail.id),
    name: detail.label,
    description: detail.description,
    buckets,
    sequenceStatus: detail.sequence_status,
    filledRequired: detail.filled_required,
    totalRequired: detail.total_required,
    overallStatus: detail.overall_status,
    progressPercent: detail.progress_percent,
    temporalAssets: {
      before: resolve(detail.temporal?.before ?? []),
      during: resolve(detail.temporal?.during ?? []),
      after: resolve(detail.temporal?.after ?? []),
    },
  };
}

interface CoverageContextValue {
  filteredHubs: ProblemHub[];
  hubsLoading: boolean;
  hubsFetching: boolean;
  hubsError: boolean;
  activeFilter: CoverageFilter;
  setActiveFilter: (filter: CoverageFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedHub: ProblemHub | null;
  setSelectedHub: (hub: ProblemHub | null) => void;
  /** True while hub detail/assets have no usable payload yet (initial load or hub switch). */
  selectedHubLoading: boolean;
  /** True while hub detail/assets are refetching with existing data. */
  selectedHubFetching: boolean;
  kpiData: KpiData;
  summaryLoading: boolean;
  summaryFetching: boolean;
  filterCounts: Record<CoverageFilter, number>;
  promotionsByUser: UserPromotionData[];
  activityLoading: boolean;
  activityFetching: boolean;
  promotionDateRange: PromotionDateRange;
  setPromotionDateRange: (range: PromotionDateRange) => void;
  totalPromotions: number;
  hubWorkFiles: CoverageWorkFile[];
  workFilesLoading: boolean;
  workFilesFetching: boolean;
  handleGenerateWorkFile: (hub: ProblemHub, name: string) => Promise<void>;
  demoteAsset: (assetId: number) => Promise<void>;
  removeContentRole: (assetId: number, tagId: number) => Promise<void>;
  mutationBusy: boolean;
  invalidateAfterWorkFile: (hubId: number) => void;
}

const CoverageContext = createContext<CoverageContextValue | undefined>(undefined);

const EMPTY_FILTER_COUNTS: Record<CoverageFilter, number> = {
  all: 0,
  output_ready: 0,
  partial: 0,
  empty: 0,
  sequence_ready: 0,
};

export const CoverageProvider = ({ children }: { children: ReactNode }) => {
  const { selectedBoard } = useSwivuBryntumContext();
  const slug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const boardId = selectedBoard?.id;
  const { pinTask } = useAsyncTaskContext();

  const [activeFilter, setActiveFilter] = useState<CoverageFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedHubId, setSelectedHubId] = useState<number | null>(null);
  const [promotionDateRange, setPromotionDateRange] = useState<PromotionDateRange>("1m");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  // Reset selection when board changes.
  useEffect(() => {
    setSelectedHubId(null);
    setActiveFilter("all");
    setSearchQuery("");
  }, [slug]);

  const summaryQuery = useCoverageSummaryQuery(slug, !!slug);
  const hubsQuery = useCoverageHubsQuery(slug, activeFilter, debouncedSearch, !!slug);
  const activityQuery = useCoverageActivityQuery(slug, promotionDateRange, !!slug);
  const hubDetailQuery = useCoverageHubDetailQuery(slug, selectedHubId, selectedHubId != null);
  const hubAssetsQuery = useCoverageHubAssetsQuery(slug, selectedHubId, {}, selectedHubId != null);
  const workFilesQuery = useCoverageHubWorkFilesQuery(slug, selectedHubId, {}, selectedHubId != null);

  const {
    removeContentRole: removeRoleMutation,
    demoteAsset: demoteMutation,
    generateWorkFile: generateMutation,
    invalidateAfterWorkFile,
  } = useCoverageMutations(slug, selectedHubId, boardId);

  // Close detail if selected hub vanished (404 / filter).
  useEffect(() => {
    if (hubDetailQuery.isError && selectedHubId != null) {
      setSelectedHubId(null);
      void hubsQuery.refetch();
    }
  }, [hubDetailQuery.isError, selectedHubId, hubsQuery]);

  const filteredHubs = useMemo(
    () => (hubsQuery.data?.results ?? []).map(mapHubRowToProblemHub),
    [hubsQuery.data]
  );

  const assets = useMemo(
    () => (hubAssetsQuery.data?.results ?? []).map(mapApiAsset),
    [hubAssetsQuery.data]
  );

  const selectedHub = useMemo(() => {
    if (selectedHubId == null) return null;
    if (hubDetailQuery.data) {
      return buildSelectedProblemHub(hubDetailQuery.data, assets);
    }
    return filteredHubs.find((h) => Number(h.id) === selectedHubId) ?? null;
  }, [selectedHubId, hubDetailQuery.data, assets, filteredHubs]);

  const kpiData = useMemo<KpiData>(() => {
    const p = summaryQuery.data?.progress;
    if (!p) {
      return {
        requiredFilled: 0,
        requiredTotal: 0,
        requiredPercent: 0,
        outputReadyCount: 0,
        sequenceReadyCount: 0,
        blindSpotCount: 0,
        hubsTotal: 0,
      };
    }
    return {
      requiredFilled: p.required_filled,
      requiredTotal: p.required_total,
      requiredPercent: p.required_percent,
      outputReadyCount: p.output_ready_count,
      sequenceReadyCount: p.sequence_ready_count,
      blindSpotCount: p.blind_spot_count,
      hubsTotal: p.hubs_total,
    };
  }, [summaryQuery.data]);

  const filterCounts = useMemo(() => {
    const fc = summaryQuery.data?.filter_counts;
    if (!fc) return EMPTY_FILTER_COUNTS;
    return {
      all: fc.all,
      output_ready: fc.output_ready,
      partial: fc.partial,
      empty: fc.empty,
      sequence_ready: fc.sequence_ready,
    };
  }, [summaryQuery.data]);

  const promotionsByUser = useMemo<UserPromotionData[]>(() => {
    return (activityQuery.data?.results ?? []).map((row) => ({
      userId: row.user_id,
      name: row.name,
      profileImageURL: row.profile_image_url ?? undefined,
      promotions: row.promotions,
      promotedAssets: (row.promoted_assets ?? []).map((a) => ({
        id: a.id,
        caption: a.caption ?? "",
        createdAt: new Date(a.promoted_at),
        tagLabels: a.tag_labels ?? [],
        problemHubLabels: a.problem_hub_labels ?? [],
      })),
    }));
  }, [activityQuery.data]);

  // Deferred exact board-wide total — sum of loaded activity page only.
  const totalPromotions = useMemo(
    () => promotionsByUser.reduce((sum, u) => sum + u.promotions, 0),
    [promotionsByUser]
  );

  const hubWorkFiles = workFilesQuery.data?.results ?? [];

  const handleSetSelectedHub = useCallback((hub: ProblemHub | null) => {
    setSelectedHubId(hub ? Number(hub.id) : null);
  }, []);

  const handleGenerateWorkFile = useCallback(
    async (hub: ProblemHub, name: string) => {
      if (!slug || boardId == null) {
        showToast({ msg: "No board selected.", type: "error", duration: 3000 });
        throw new Error("Board slug is not available");
      }
      try {
        const response = await generateMutation.mutateAsync({
          problemHubId: Number(hub.id),
          name: name.trim(),
        });
        const taskId = extractAsyncTaskId(response.data);
        if (taskId != null) {
          pinTask(taskId, {
            type: ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION,
            meta_data: {
              board_id: boardId,
              coverage_id: Number(hub.id),
            },
          });
        }
      } catch {
        showToast({ msg: "Failed to start work file generation.", type: "error", duration: 3000 });
        throw new Error("Work file generation failed");
      }
    },
    [slug, boardId, pinTask, generateMutation]
  );

  const demoteAsset = useCallback(
    async (assetId: number) => {
      try {
        await demoteMutation.mutateAsync(assetId);
      } catch {
        showToast({ msg: "Failed to demote asset.", type: "error", duration: 3000 });
        throw new Error("Demote failed");
      }
    },
    [demoteMutation]
  );

  const removeContentRole = useCallback(
    async (assetId: number, tagId: number) => {
      try {
        await removeRoleMutation.mutateAsync({ assetId, tagId });
      } catch {
        showToast({ msg: "Failed to remove content role.", type: "error", duration: 3000 });
        throw new Error("Remove role failed");
      }
    },
    [removeRoleMutation]
  );

  const value: CoverageContextValue = {
    filteredHubs,
    hubsLoading: hubsQuery.isLoading,
    hubsFetching: hubsQuery.isFetching,
    hubsError: hubsQuery.isError,
    activeFilter,
    setActiveFilter,
    searchQuery,
    setSearchQuery,
    selectedHub,
    setSelectedHub: handleSetSelectedHub,
    selectedHubLoading: Boolean(
      selectedHubId != null &&
        (hubDetailQuery.isLoading ||
          hubAssetsQuery.isLoading ||
          !hubDetailQuery.data)
    ),
    selectedHubFetching: Boolean(
      selectedHubId != null &&
        !!hubDetailQuery.data &&
        (hubDetailQuery.isFetching || hubAssetsQuery.isFetching)
    ),
    kpiData,
    summaryLoading: summaryQuery.isLoading,
    summaryFetching: summaryQuery.isFetching,
    filterCounts,
    promotionsByUser,
    activityLoading: activityQuery.isLoading,
    activityFetching: activityQuery.isFetching,
    promotionDateRange,
    setPromotionDateRange,
    totalPromotions,
    hubWorkFiles,
    workFilesLoading: workFilesQuery.isLoading,
    workFilesFetching: workFilesQuery.isFetching,
    handleGenerateWorkFile,
    demoteAsset,
    removeContentRole,
    mutationBusy:
      removeRoleMutation.isPending || demoteMutation.isPending || generateMutation.isPending,
    invalidateAfterWorkFile: (hubId: number) => {
      void invalidateAfterWorkFile(hubId);
    },
  };

  return <CoverageContext.Provider value={value}>{children}</CoverageContext.Provider>;
};

export const useCoverage = () => {
  const context = useContext(CoverageContext);
  if (!context) {
    throw new Error("useCoverage must be used within a CoverageProvider");
  }
  return context;
};
